import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { CcProject, CcTask } from "@shared/schema";
import { ProjectFormModal } from "@/components/project-form-modal";
import { TaskFormModal } from "@/components/task-form-modal";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  medium: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400",
  low: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400",
};

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CcTask | undefined>();
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Fetch project
  const { data: project, isLoading: projectLoading } = useQuery<CcProject>({
    queryKey: [`/api/control-centre/projects/${id}`],
    enabled: !!id,
  });

  // Fetch tasks for this project
  const { data: projectTasks = [], isLoading: tasksLoading, refetch: refetchTasks } = useQuery<CcTask[]>({
    queryKey: [`/api/control-centre/tasks?projectId=${id}`],
    enabled: !!id,
  });

  const deleteProjectMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/control-centre/projects/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => 
          query.queryKey.some(segment => 
            segment?.toString().startsWith("/api/control-centre/projects")
          )
      });
      toast({ title: "Success", description: "Project deleted successfully" });
      navigate("/project-manager/projects");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete project",
        variant: "destructive",
      });
    },
  });

  if (!id) return <div>Invalid project</div>;

  const isLoading = projectLoading || tasksLoading;

  // Filter tasks by status
  const filteredTasks =
    filterStatus === "all"
      ? projectTasks
      : projectTasks.filter((t) => t.status === filterStatus);

  // Group tasks by status
  const tasksByStatus = {
    not_started: filteredTasks.filter((t) => t.status === "not_started"),
    in_progress: filteredTasks.filter((t) => t.status === "in_progress"),
    blocked: filteredTasks.filter((t) => t.status === "blocked"),
    review: filteredTasks.filter((t) => t.status === "review"),
    completed: filteredTasks.filter((t) => t.status === "completed"),
  };

  const completedCount = projectTasks.filter(
    (t) => t.status === "completed"
  ).length;
  const totalCount = projectTasks.length;

  const handleAddTask = () => {
    setSelectedTask(undefined);
    setIsTaskModalOpen(true);
  };

  const handleEditTask = (task: CcTask) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title={project?.projectName || "Loading..."}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Project Manager" },
          { label: "Projects", href: "/project-manager/projects" },
          { label: project?.projectName || "Project" },
        ]}
        actions={
          <div className="flex gap-2">
            <Button onClick={handleAddTask} data-testid="button-add-task">
              <Plus className="mr-2 h-4 w-4" />
              Add Task
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsProjectModalOpen(true)}
              data-testid="button-edit-project"
            >
              Edit Project
            </Button>
          </div>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !project ? (
          <Card className="mx-auto max-w-2xl p-8 text-center">
            <p className="text-destructive font-medium">Project not found</p>
          </Card>
        ) : (
          <div className="mx-auto max-w-7xl space-y-6">
            {/* Project Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{project.projectName}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-2">
                      {project.projectDescription}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteProjectMutation.mutate()}
                    disabled={deleteProjectMutation.isPending}
                    data-testid="button-delete-project"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Status</div>
                    <Badge variant="secondary" className="mt-1">
                      {project.status}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Priority</div>
                    <Badge variant="secondary" className="mt-1">
                      {project.priority}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Type</div>
                    <Badge variant="secondary" className="mt-1">
                      {project.projectType.replace("_", " ")}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Target End</div>
                    <p className="text-sm font-medium mt-1">
                      {project.targetEndDate || "Not set"}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Progress</span>
                    <span className="text-sm font-semibold">
                      {project.progressPercentage}%
                    </span>
                  </div>
                  <Progress value={project.progressPercentage} className="h-2" />
                </div>

                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Tasks</span>
                    <span className="font-medium">
                      {completedCount} / {totalCount} completed
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                Filter by status:
              </span>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="select-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tasks Kanban View */}
            {filteredTasks.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No tasks in this project</p>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-5">
                {(
                  ["not_started", "in_progress", "blocked", "review", "completed"] as const
                ).map((status) => {
                  const statusTasks = tasksByStatus[status];
                  const statusLabel = status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());

                  return (
                    <Card key={status} data-testid={`column-${status}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-semibold">
                            {statusLabel}
                          </CardTitle>
                          <Badge variant="secondary">{statusTasks.length}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {statusTasks.map((task) => (
                          <Card
                            key={task.id}
                            className="p-3 hover-elevate cursor-pointer"
                            onClick={() => handleEditTask(task)}
                            data-testid={`card-task-${task.id}`}
                          >
                            <h4 className="font-semibold text-sm mb-2 line-clamp-2">
                              {task.taskTitle}
                            </h4>
                            <div className="flex flex-wrap gap-1 mb-2">
                              <Badge
                                variant="outline"
                                className={`text-xs ${PRIORITY_COLORS[task.priority as keyof typeof PRIORITY_COLORS]}`}
                              >
                                {task.priority}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {task.effort}
                              </Badge>
                              {task.category && (
                                <Badge variant="secondary" className="text-xs">
                                  {task.category}
                                </Badge>
                              )}
                            </div>
                            {task.dueDate && (
                              <div className="text-xs text-muted-foreground">
                                Due: {task.dueDate}
                              </div>
                            )}
                          </Card>
                        ))}
                        {statusTasks.length === 0 && (
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            No tasks
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {project && (
        <>
          <ProjectFormModal
            open={isProjectModalOpen}
            onOpenChange={setIsProjectModalOpen}
            project={project}
          />
          <TaskFormModal
            open={isTaskModalOpen}
            onOpenChange={(newOpen) => {
              setIsTaskModalOpen(newOpen);
              if (!newOpen) refetchTasks();
            }}
            task={selectedTask}
            defaultProjectId={id}
          />
        </>
      )}
    </div>
  );
}
